import React, { forwardRef, useRef, useImperativeHandle } from 'react';
import './Input.css';
import {IntentType} from '../Intent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';

export interface InputProps {
  /**
   * The id of the input.
   */
  id: string;
  /**
   * The value of the input. If undefined, the input will be uncontrolled.
   */
  value?: string;
  /**
   * The default value of the input. If undefined, the input will be controlled.
   */
  defaultValue?: string;
  /**
   * The class name of the input container, and the input
   * @default ""
   * */
  className?: string;
  inputClassName?: string;
  /**
   * The type of the input
   * @default "text"
   * */
  type?:
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'month'
    | 'number'
    | 'password'
    | 'search'
    | 'tel'
    | 'text'
    | 'url'
    | 'week';
  /**
   * The if the input is full width
   * @default false
   * */
  fullWidth?: boolean;
  /**
   * The CSS style properties of the input
   * @default {}
   * */
  style?: React.CSSProperties;
  /**
   * The intent of the input
   * @default "none"
   * */
  intent?: IntentType;
  /**
   * The placeholder of the input
   * @default ""
   * */
  placeholder?: string;
  /**
   * Whether the input is disabled
   * @default false
   * */
  disabled?: boolean;
  /**
   * The size of the input.
   * @default "default"
   * */
  size?: 'small' | 'default' | 'large';
  /**
   * The ref of the input if you need direct access to the element
   * @default null
   * */
  ref?: React.LegacyRef<HTMLInputElement>;
  /**
   * The icon on the left of the input
   * @default null
   * */
  iconLeft?: IconProp;
  /**
   * The icon on the right of the input
   * @default null
   * */
  iconRight?: IconProp;
  /**
   * The element on the left of the input. This can be used to render a custom element like a button. This prop will override the iconLeft prop. Padding on the input should be handled by the custom element via className or style properties.
   * @default null
   */
  leftElement?: React.ReactNode;
  /**
   * The element on the right of the input. This can be used to render a custom element like a button. This prop will override the iconRight prop. Padding on the input should be handled by the custom element via className or style properties.
   * @default null
   */
  rightElement?: React.ReactNode;
  /**
   * The autoComplete attribute of the input
   * @default "off"
   * */
  autoComplete?: string;
  /**
   * The autoFocus attribute of the input
   * */
  autoFocus?: boolean;
  /**
   * The change event handler
   * */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /**
   * The focus event handler
   * */
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  /**
   * The blur event handler
   * */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /**
   * The key down event handler
   * */
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  /**
   * The key up event handler
   * */
  onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>;
  /**
   * The other associated HTML props of the input
   */
  htmlAttributes?: React.InputHTMLAttributes<HTMLInputElement>
}

/**
 * The input component is the most basic form element. It can be used to create text inputs, number inputs, search inputs, and more.
 * */
const Input: React.FC<InputProps> = forwardRef(function Input(props: InputProps, ref) {
  const {
    autoComplete= 'off',
    autoFocus,
    className,
    inputClassName,
    disabled= false,
    fullWidth= false,
    htmlAttributes,
    iconLeft,
    iconRight,
    id,
    intent= 'none',
    leftElement,
    placeholder,
    rightElement,
    size= 'default',
    style,
    type= 'text',
    value,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    onKeyUp,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(ref, () => inputRef.current);

  const renderLeftElement = (): React.ReactNode => {
    if (leftElement) {
      return <div className={'element-left'}>{leftElement}</div>;
    } else if (iconLeft) {
      return <FontAwesomeIcon icon={iconLeft} className="icon-left" />;
    }
    return null;
  };

  const renderRightElement = (): React.ReactNode => {
    if (rightElement) {
      return <div className={'element-right'}>{rightElement}</div>;
    } else if (iconRight) {
      return <FontAwesomeIcon icon={iconRight} className="icon-right" />;
    }
    return null;
  };

  const handleClickTargetClick = (): void => {
    inputRef?.current?.focus();
  };

  const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {
    ...htmlAttributes,
    id,
    name: id,
    type,
    style,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    onKeyUp,
    disabled,
    placeholder,
    autoComplete,
    autoFocus,
    className: inputClassName
  };

  if (value !== undefined) {
    inputProps.value = value;
  }
  if (props.defaultValue !== undefined) {
    inputProps.defaultValue = props.defaultValue;
  }

  return (
    <div
      className={`input-container ${className ?? ''} ${'size-' + size} ${'intent-' + intent} ${
        fullWidth ? 'full-width' : ''
      } ${iconLeft && !leftElement ? 'has-icon-left' : ''} ${
        iconRight && !rightElement ? 'has-icon-right' : ''
      }
    `}
    >
      {size === 'small' && !disabled ? (
        <div className={'clickTargetExpander'} onClick={handleClickTargetClick} role="none" />
      ) : null}
      {renderRightElement()}
      {renderLeftElement()}
      <input className={inputClassName} {...inputProps} ref={inputRef} />
    </div>
  );
});

export default Input;
